import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { type Sessao, type UsuarioAtual } from '@fiscoai/contracts';

import { buscarUsuarioAtual, encerrarNoServidor } from './api';
import { AuthContext, type ContextoAuth, type EstadoAuth } from './contexto';
import { sessao as sessaoPadrao, type SessaoStore } from './sessao';

interface Props {
  children: ReactNode;
  /** Injetável nos testes. */
  sessao?: SessaoStore;
}

/** Restaura a sessão ao abrir (se houver) e expõe login/logout para a árvore. */
export function AuthProvider({ children, sessao = sessaoPadrao }: Props) {
  const queryClient = useQueryClient();
  const [estado, setEstado] = useState<EstadoAuth>('carregando');
  const [usuario, setUsuario] = useState<UsuarioAtual | null>(null);

  const carregarUsuario = useCallback(async () => {
    const atual = await buscarUsuarioAtual();
    setUsuario(atual);
    setEstado('autenticado');
  }, []);

  useEffect(() => {
    let ativo = true;
    const restaurar = async () => {
      if (!sessao.provavelmenteAtiva() || !(await sessao.renovar())) {
        if (ativo) setEstado('anonimo');
        return;
      }
      await carregarUsuario().catch(() => {
        if (ativo) setEstado('anonimo');
      });
    };
    void restaurar();
    const cancelar = sessao.aoExpirar(() => {
      setUsuario(null);
      setEstado('anonimo');
      queryClient.clear();
    });
    return () => {
      ativo = false;
      cancelar();
    };
  }, [sessao, carregarUsuario, queryClient]);

  const entrar = useCallback(
    async (nova: Sessao) => {
      sessao.iniciar(nova);
      await carregarUsuario();
    },
    [sessao, carregarUsuario],
  );

  const sair = useCallback(async () => {
    await encerrarNoServidor().catch(() => undefined);
    sessao.encerrar();
    setUsuario(null);
    setEstado('anonimo');
    queryClient.clear();
  }, [sessao, queryClient]);

  const valor = useMemo<ContextoAuth>(
    () => ({ estado, usuario, entrar, sair, recarregarUsuario: carregarUsuario }),
    [estado, usuario, entrar, sair, carregarUsuario],
  );
  return <AuthContext value={valor}>{children}</AuthContext>;
}
